import React, { useRef, useState } from "react";
import { Control, Controller, Field, FieldErrors } from "react-hook-form";
import { StyleSheet, TextInput as RNTextInput } from "react-native";
import Colors from "../../constants/Colors";
import { View, Text } from "../Themed";
import { Feather } from "@expo/vector-icons";
import MaskInput from "react-native-mask-input";
import Pressable from "../Pressable";

interface InputTextProps {
  control: Control;
  label: string;
  name: string;
  errors: FieldErrors;
  placeholder?: string;
  required?: boolean;
  multiline?: boolean;
  disabled?: boolean;
  secureTextEntry?: boolean;
  onBlurInput?: (value: string) => void;
  onChangeText?: (value: string) => void;
  onSubmitEditing?: () => void;
  type?: "default" | "numeric" | "email-address" | "phone-pad";
  autoCapitalize?: "none" | "sentences" | "words" | "characters";
  testingID?: string;
}

const InputText: React.FC<InputTextProps> = ({
  control,
  label,
  name,
  errors,
  placeholder = "",
  required = false,
  multiline = false,
  disabled = false,
  onBlurInput,
  onChangeText,
  onSubmitEditing,
  secureTextEntry = false,
  type = "default",
  autoCapitalize = "sentences",
  testingID,
}) => {
  const inputRef = useRef<RNTextInput>(null);
  const [formState, setFormState] = useState<"default" | "active" | "error">(
    "default"
  );

  const [isSecureTextEntry, setIsSecureTextEntry] = useState(secureTextEntry);

  return (
    <View
      style={{
        width: "100%",
        flex: 1,
      }}
    >
      <Controller
        control={control}
        rules={{
          required: required ? "This field is required" : false,
        }}
        render={({ field: { onChange, onBlur, value } }) => (
          <View style={[styles.body]}>
            <View style={styles.labelRow}>
              <Text style={styles.labelText}>{label}</Text>
              {required && <Text style={styles.errorStar}>*</Text>}
            </View>
            <Pressable
              disabled={disabled}
              hitSlop={styles.hit}
              tabIndex={-1}
              onPress={() => {
                inputRef.current?.focus();
              }}
              style={[
                styles.input,
                errors[name] !== undefined
                  ? styles.inputError
                  : formState === "active"
                  ? styles.inputActive
                  : null,
                {
                  backgroundColor: disabled
                    ? Colors.nuetral_grey_200
                    : Colors.nuetral_grey_100,
                },
              ]}
              testID={testingID}
            >
              {name === "phone" ? (
                <MaskInput
                  ref={inputRef}
                  onBlur={() => {
                    onBlur();
                    onBlurInput && onBlurInput(value);
                    setFormState("default");
                  }}
                  autoCapitalize={autoCapitalize}
                  secureTextEntry={isSecureTextEntry}
                  editable={!disabled}
                  multiline={multiline}
                  onFocus={() => setFormState("active")}
                  onChangeText={(masked, unmasked) => {
                    onChangeText && onChangeText(masked);
                    onChange(masked);
                  }}
                  value={value}
                  keyboardType={type}
                  allowFontScaling={false}
                  maxFontSizeMultiplier={1}
                  style={[
                    styles.inputText,
                    multiline && { height: 100 },
                    { color: disabled ? Colors.nuetral_grey_600 : "black" },
                  ]}
                  mask={[
                    "(",
                    /\d/,
                    /\d/,
                    /\d/,
                    ")",
                    /\d/,
                    /\d/,
                    /\d/,
                    "-",
                    /\d/,
                    /\d/,
                    /\d/,
                    /\d/,
                  ]}
                  placeholder={placeholder}
                  placeholderTextColor={Colors.nuetral_grey_600}
                />
              ) : (
                <RNTextInput
                  ref={inputRef}
                  onBlur={() => {
                    onBlur();
                    onBlurInput && onBlurInput(value);
                    setFormState("default");
                  }}
                  autoCapitalize={autoCapitalize}
                  secureTextEntry={isSecureTextEntry}
                  editable={!disabled}
                  multiline={multiline}
                  onFocus={() => setFormState("active")}
                  onChangeText={(v) => {
                    onChangeText && onChangeText(v);
                    onChange(v);
                  }}
                  onSubmitEditing={onSubmitEditing}
                  value={value}
                  keyboardType={type}
                  allowFontScaling={false}
                  maxFontSizeMultiplier={1}
                  style={[
                    styles.inputText,
                    multiline && {
                      height: 100,
                    },
                    {
                      color: disabled ? Colors.nuetral_grey_600 : "black",
                    },
                  ]}
                  placeholder={placeholder}
                  placeholderTextColor={Colors.nuetral_grey_600}
                />
              )}
              {secureTextEntry && (
                <Pressable
                  accessibilityLabel={
                    isSecureTextEntry ? "Show password" : "Hide password"
                  }
                  accessibilityRole="button"
                  hitSlop={styles.hit}
                  style={styles.hide}
                  onPress={() => setIsSecureTextEntry(!isSecureTextEntry)}
                >
                  <Feather
                    name={isSecureTextEntry ? "eye" : "eye-off"}
                    size={18}
                    color={Colors.primary_500}
                  />
                </Pressable>
              )}
            </Pressable>
          </View>
        )}
        name={name}
      />
      {errors[name] ? (
        <View style={styles.errorContainer}>
          <Text lineHeight={14} style={styles.errorText}>
            {errors[name]?.message}
          </Text>
        </View>
      ) : (
        <Text style={styles.space} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  body: {
    flex: 1,
  },
  input: {
    backgroundColor: Colors.nuetral_grey_100,
    borderWidth: 1,
    borderRadius: 5,
    borderColor: Colors.nuetral_grey_400,
  },
  labelText: {
    fontSize: 16,
    paddingBottom: 4,
    color: Colors.nuetral_grey_700,
  },
  errorStar: {
    fontSize: 18,
    marginBottom: -2,
    color: "#d42121",
  },
  inputText: {
    fontSize: 14,
    color: "black",
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  inputError: {
    borderColor: "#d42121",
  },
  inputActive: {
    borderColor: Colors.primary_500,
  },
  hide: {
    position: "absolute",
    right: 10,
    top: 10,
  },
  hit: {
    top: 10,
    bottom: 10,
    left: 10,
    right: 10,
  },
  errorText: {
    color: "#d42121",
    fontSize: 12,
  },
  errorContainer: {
    height: 18,
    flexDirection: "row",
    alignItems: "center",
  },
  space: {
    height: 18,
  },
  labelRow: {
    flexDirection: "row",
  },
});

export default InputText;
